import { CertificationReview } from '@/modules/certification/components/CertificationReview';
import { CertificationTest } from '@/modules/certification/components/CertificationTest';
import { useCertificationStatus } from '@/modules/certification/hooks/useCertificationStatus';

import { useCaseDetails } from '../hooks/useCaseDetails';

interface CertificationModalProps {
  /** Database ID of the case */
  caseId: string;
  /** Masterclass ID for loading case metadata */
  masterclassId?: string;
  userId: string;
  onClose: () => void;
}

/**
 * Router component that decides whether to show CertificationTest or CertificationReview
 * based on the user's certification status.
 *
 * Logic:
 * - If user has passed: Show CertificationReview (read-only mode with answers)
 * - Otherwise: Show CertificationTest (normal test mode)
 */
export const CertificationModal = ({
  caseId,
  masterclassId,
  userId,
  onClose,
}: CertificationModalProps) => {
  const { status, loading } = useCertificationStatus(caseId, userId, masterclassId || null);

  // caseId is now the database ID, use it directly
  const { details: caseDetails, loading: caseDetailsLoading } = useCaseDetails(caseId);

  // Show loading state while checking certification status
  if (loading || caseDetailsLoading || !caseDetails) {
    return null;
  }

  // If user has already passed, show review mode
  if (status.passed) {
    return (
      <CertificationReview
        caseId={caseId}
        masterclassId={masterclassId}
        caseTitle={caseDetails.displayName}
        userId={userId}
        onClose={onClose}
      />
    );
  }

  // Otherwise show normal test mode
  return (
    <CertificationTest
      caseId={caseId}
      masterclassId={masterclassId}
      userId={userId}
      onClose={onClose}
      onTestComplete={() => {
        // Trigger refresh of case cards to update certification button
        window.dispatchEvent(
          new CustomEvent('certification-updated', {
            detail: { caseId },
          })
        );
        onClose();
      }}
    />
  );
};
